/**
 * ExamResultsPage.tsx
 * Modern, scientific exam results page with comprehensive analysis
 * Uses VSAT Theme System for consistent UI/UX
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { toast } from 'sonner';
import { apiService } from '@vsat/shared/lib/api';
import {
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  User,
  Calendar,
  FileText,
  Award,
  Home,
  RotateCcw,
  AlertCircle,
  Mail,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@vsat/shared/components';
import { Button } from '@vsat/shared/components';
import { Badge } from '@vsat/shared/components';
import { SEOHead } from '../components/SEOHead';
import { Progress } from '@vsat/shared/components';
import { HtmlContent } from '../components/exam/HtmlContent';
import DemoResults from '../components/exam/DemoResults';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { AiGradingWait } from '../components/exam/results/AiGradingWait';

// Helper function to format score with max 2 decimal places
const formatScore = (score: number): string => {
  // Round to 2 decimal places and remove trailing zeros
  const rounded = Math.round(score * 100) / 100;
  return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(2).replace(/\.?0+$/, '');
};

export default function ExamResultsPage() {
  const navigate = useNavigate();
  const { examId, resultId: resultIdFromUrl } = useParams();
  const { user } = useAuthStore();
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Read sessionStorage once and cache
  const storedResultsRef = useRef(sessionStorage.getItem('examResults'));
  const storedResults = storedResultsRef.current;

  interface ResultsData {
    summary: any;
    examInfo: any;
    questions: any[];
    answers: any;
    resultId?: string;
  }

  const [apiResultsData, setApiResultsData] = useState<ResultsData | null>(null);
  const [apiLoading, setApiLoading] = useState(false);

  // AI grading state  -  merge AI score after polling completes
  const [aiGradingDone, setAiGradingDone] = useState(false);
  const [aiGradingResult, setAiGradingResult] = useState<any>(null);
  const [showAiWaiting, setShowAiWaiting] = useState(false);

  // Fetch from API when resultId is in URL (history view)
  useEffect(() => {
    if (resultIdFromUrl) {
      setApiLoading(true);
      apiService
        .getExamResultDetail(resultIdFromUrl)
        .then((result: any) => {
          const gradeSummary = result.gradeSummary;
          if (gradeSummary) {
            setApiResultsData({
              summary: { backendGrade: gradeSummary },
              examInfo: {
                title: result.examTitle,
                subject: result.subject,
                passingScore: result.exam?.passingScore,
              },
              questions: [],
              answers: {},
              resultId: result.id,
            });
          } else {
            setApiResultsData({
              summary: {
                backendGrade: {
                  examId: result.examId,
                  sessionId: '',
                  totalQuestions: result.totalQuestions,
                  answeredQuestions: result.totalQuestions,
                  correctCount: result.correctAnswers,
                  incorrectCount: result.totalQuestions - result.correctAnswers,
                  unansweredCount: 0,
                  score: result.score,
                  maxScore: result.exam?.standard === 'THPT' ? 10 : 150,
                  passingScore: result.exam?.passingScore || 0,
                  percentage: result.percentage,
                  passed: result.passed,
                  timeSpent: result.timeSpent,
                  completedAt: result.createdAt,
                  questionGrades: [],
                  hasEssayQuestions: false,
                  allowViewDetailedResults: false,
                },
              },
              examInfo: {
                title: result.examTitle,
                subject: result.subject,
                passingScore: result.exam?.passingScore,
              },
              questions: [],
              answers: {},
              resultId: result.id,
            });
          }
        })
        .catch(err => {
          console.error('Failed to fetch result detail:', err);
          toast.error('Không thể tải kết quả bài thi');
          navigate('/thi-thu-thpt', { replace: true });
        })
        .finally(() => setApiLoading(false));
    }
  }, [resultIdFromUrl, navigate]);

  // If no results source at all, redirect
  useEffect(() => {
    if (!storedResults && !resultIdFromUrl) {
      toast.error('Không tìm thấy kết quả bài thi');
      navigate('/thi-thu-thpt', { replace: true });
    }
  }, [storedResults, resultIdFromUrl, navigate]);

  // Determine effective resultId early  -  must be before any early returns (Rules of Hooks)
  const effectiveResultId = resultIdFromUrl || apiResultsData?.resultId || '';

  // useCallback hooks  -  must be before any early returns (Rules of Hooks)
  const handleAiCompleted = useCallback(
    (result: any) => {
      setAiGradingResult(result);
      setAiGradingDone(true);
      setShowAiWaiting(false);
      if (effectiveResultId) sessionStorage.removeItem(`ai_grading_${effectiveResultId}`);
    },
    [effectiveResultId]
  );

  const handleAiFailed = useCallback(() => {
    setShowAiWaiting(false);
    if (effectiveResultId) sessionStorage.removeItem(`ai_grading_${effectiveResultId}`);
  }, [effectiveResultId]);

  // Loading state
  if (apiLoading || (resultIdFromUrl && !apiResultsData)) {
    return (
      <div className='bg-background-light dark:bg-background-dark min-h-screen flex flex-col'>
        <SEOHead
          title='Kết quả bài thi | Hướng nghiệp CAT'
          description='Xem kết quả bài thi thử TN THPT.'
          noindex
        />
        <UnifiedHeader />
        <main className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
            <p className='text-gray-600 dark:text-gray-400'>Đang tải kết quả...</p>
          </div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  // Determine data source
  const resultsData: ResultsData | null =
    resultIdFromUrl && apiResultsData
      ? apiResultsData
      : storedResults
        ? (() => {
            try {
              return JSON.parse(storedResults);
            } catch {
              return null;
            }
          })()
        : apiResultsData;

  if (!resultsData) {
    return (
      <div className='bg-background-light dark:bg-background-dark min-h-screen flex flex-col'>
        <SEOHead
          title='Kết quả bài thi | Hướng nghiệp CAT'
          description='Xem kết quả bài thi thử TN THPT.'
          noindex
        />
        <UnifiedHeader />
        <main className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
            <p className='text-gray-600 dark:text-gray-400'>Đang tải kết quả...</p>
          </div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }
  const { summary, examInfo, questions: questionsFromResults } = resultsData;

  // Try to get questions from multiple sources
  // Priority: localStorage (has full data with correct answers) > resultsData.questions
  let questions = questionsFromResults;

  // Always try to load from localStorage first (has complete question data with correct answers)
  try {
    const storedSession = localStorage.getItem('currentExamSession');
    if (storedSession) {
      const sessionData = JSON.parse(storedSession);
      if (sessionData?.exam?.clusters) {
        // Flatten questions from clusters (same logic as DemoExamPage.mapExamToDemoData)
        const flattened: any[] = [];
        sessionData.exam.clusters.forEach((cluster: any) => {
          const clusterQuestions = cluster.clusterQuestions || [];
          clusterQuestions.forEach((q: any) => {
            if (q && q.id) {
              // Parse content if it's a string
              let parsedContent: any = {};
              if (q.content && typeof q.content === 'string') {
                try {
                  parsedContent = JSON.parse(q.content);
                } catch {
                  parsedContent = {};
                }
              } else if (q.content) {
                parsedContent = q.content;
              }

              flattened.push({
                id: q.id,
                type: q.type,
                stem: q.stem || q.context || '',
                content: parsedContent,
                subject: q.subject || examInfo?.subject,
                points: q.points || 0,
                options: q.options || [],
                correct: q.correct,
                correctAnswer: q.correctAnswer,
                // Matching question data
                matchingPrompts: q.matchingPrompts || parsedContent?.matchingPrompts || [],
                matchingOptions: q.matchingOptions || parsedContent?.matchingOptions || [],
                matchingCorrect:
                  q.matchingCorrect || parsedContent?.matchingCorrect || q.answer_map || {},
                // Short answer data
                numericAnswers: q.numericAnswers || parsedContent?.numericAnswers || [],
                textAnswers: q.textAnswers || parsedContent?.textAnswers || [],
                fillBlankAnswers: q.fillBlankAnswers || parsedContent?.fillBlankAnswers || [],
                correctKeywords: q.correctKeywords || parsedContent?.correctKeywords || [],
                // True/False data
                trueFalseItems: q.trueFalseItems || [],
                explanation: q.explanation || parsedContent?.explanation || '',
              });
            }
          });
        });
        if (flattened.length > 0) {
          questions = flattened;
        }
      }
    }
  } catch (error) {
    console.error('Failed to load questions from localStorage:', error);
  }

  // Check if we have backend grading results
  const backendGrade = summary.backendGrade;

  if (!backendGrade) {
    // Fallback to old DemoResults if no backend grade
    const displaySummary = summary;
    const questions = displaySummary.items.map((item: any) => ({
      id: item.questionId,
      points: item.maxPoints || item.earned,
      subject: examInfo.subject || 'Tổng hợp',
    }));

    return (
      <div className='bg-background-light dark:bg-background-dark min-h-screen flex flex-col'>
        <SEOHead
          title='Kết quả bài thi | Hướng nghiệp CAT'
          description='Xem kết quả bài thi thử TN THPT.'
          noindex
        />
        <UnifiedHeader />
        <main className='flex-1 pt-20 pb-12 lg:pt-24'>
          <DemoResults
            summary={displaySummary}
            questions={questions}
            answers={resultsData.answers || {}}
            onTryAgain={() => {
              sessionStorage.removeItem('examResults');
              localStorage.removeItem('currentExamSession');
              localStorage.removeItem('currentExamQuestions');
              navigate('/thi-thu-thpt');
            }}
            onBackHome={() => {
              sessionStorage.removeItem('examResults');
              navigate('/thi-thu-thpt');
            }}
          />
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  // Check for pending AI grading  -  read gradingId from sessionStorage
  const pendingGradingId = effectiveResultId
    ? sessionStorage.getItem(`ai_grading_${effectiveResultId}`)
    : null;

  // Apply AI grading result to backendGrade if available
  const effectiveBackendGrade =
    aiGradingResult && aiGradingDone
      ? (() => {
          const newScore =
            backendGrade.maxScore > 0
              ? (aiGradingResult.totalScore ?? 0) +
                (backendGrade.score - (backendGrade.essayScore ?? 0))
              : backendGrade.score;
          const newPercentage = (() => {
            if (!backendGrade.maxScore) return backendGrade.percentage;
            const autoScore = backendGrade.score - (backendGrade.essayScore ?? 0);
            const newTotal = autoScore + (aiGradingResult.totalScore ?? 0);
            return Math.round((newTotal / backendGrade.maxScore) * 100);
          })();
          const threshold = backendGrade.passingScore ?? 0;
          const newPassed = threshold > 0 ? newScore >= threshold : backendGrade.passed;
          return { ...backendGrade, score: newScore, percentage: newPercentage, passed: newPassed };
        })()
      : backendGrade;

  // Use effective backend grading data (may include merged AI score)
  const percentage = Math.round(effectiveBackendGrade.percentage);
  const passingThreshold =
    typeof backendGrade.passingScore === 'number'
      ? backendGrade.passingScore
      : typeof examInfo?.passingScore === 'number'
        ? examInfo.passingScore
        : null;
  const hasPassingScore =
    typeof passingThreshold === 'number' && !Number.isNaN(passingThreshold) && passingThreshold > 0;
  // Prefer effectiveBackendGrade.passed, fallback to dynamic check against threshold
  const isPassed =
    effectiveBackendGrade.passed ??
    (hasPassingScore ? effectiveBackendGrade.score >= passingThreshold : backendGrade.passed);
  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Guest User'
    : 'Guest User';
  const completedAt = new Date(backendGrade.completedAt);

  // Check if there are essay questions in the exam
  // Use backend flag if available (preferred), otherwise fallback to client-side checks
  const hasEssayQuestionsFromBackend = backendGrade?.hasEssayQuestions ?? false;

  // Fallback checks for offline/client-side grading scenarios
  const hasEssayInBreakdown = summary.breakdown?.essay?.total > 0;
  const essayItemsCount =
    summary.items?.filter(
      (item: any) => item.questionId?.includes('essay') || item.type === 'essay'
    ).length || 0;

  // Count answered essay questions from answers object
  const answeredEssayQuestions = Object.entries(resultsData.answers || {}).filter(
    ([questionId, answer]: [string, any]) => {
      const isEssayQuestion = questionId.toLowerCase().includes('essay');
      const hasAnswer =
        answer &&
        answer.value &&
        typeof answer.value === 'string' &&
        answer.value.trim().length > 0;
      return isEssayQuestion && hasAnswer;
    }
  ).length;

  const hasEssayQuestions =
    hasEssayQuestionsFromBackend ||
    hasEssayInBreakdown ||
    essayItemsCount > 0 ||
    answeredEssayQuestions > 0;

  // Trigger AI waiting screen if essay pending and not yet resolved
  if (hasEssayQuestions && pendingGradingId && !aiGradingDone && !showAiWaiting) {
    setShowAiWaiting(true);
  }

  // Check if exam allows viewing detailed results
  // For THPT: NEVER SHOW ANSWERS AND QUESTIONS on the result screen
  const allowViewDetailedResults = false;
  const canViewDetailedResults = false; // Forced false as per user requirement

  const handleTryAgain = () => {
    sessionStorage.removeItem('examResults');
    localStorage.removeItem('currentExamSession');
    localStorage.removeItem('currentExamQuestions');
    navigate('/thi-thu-thpt');
  };

  const handleBackHome = () => {
    sessionStorage.removeItem('examResults');
    navigate('/thi-thu-thpt');
  };

  const handleSendEmail = async () => {
    const effectiveExamId = examId || (resultsData?.summary?.backendGrade as any)?.examId || '';
    const effectiveResultId = resultIdFromUrl || resultsData?.resultId || '';
    if (!effectiveExamId || !effectiveResultId) {
      toast.error('Không tìm thấy thông tin bài thi');
      return;
    }

    setIsSendingEmail(true);
    try {
      await apiService.sendResultEmail(effectiveExamId, effectiveResultId);
      toast.success('Đã gửi kết quả về email của bạn');
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Gửi email thất bại. Vui lòng thử lại sau.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Show AI grading waiting screen
  if (showAiWaiting && pendingGradingId) {
    return (
      <div className='bg-background-light dark:bg-background-dark min-h-screen flex flex-col'>
        <SEOHead
          title='Đang chấm bài | Hướng nghiệp CAT'
          description='AI đang chấm bài tự luận của bạn.'
          noindex
        />
        <UnifiedHeader />
        <main className='flex-1'>
          <AiGradingWait
            gradingId={pendingGradingId}
            onCompleted={handleAiCompleted}
            onFailed={handleAiFailed}
          />
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  return (
    <div className='bg-background-light dark:bg-background-dark min-h-screen flex flex-col text-gray-900 dark:text-gray-50'>
      <SEOHead
        title='Kết quả bài thi | Hướng nghiệp CAT'
        description='Xem kết quả bài thi thử TN THPT.'
        noindex
      />
      <UnifiedHeader />
      <main className='flex-1 pt-20 pb-12 lg:pt-24'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          {/* Header Card - Score Overview */}
          <Card className='mb-8 border border-gray-200 dark:border-gray-700 shadow-sm bg-surface-light dark:bg-surface-dark'>
            <CardContent className='p-8'>
              <div className='flex flex-col md:flex-row items-center gap-8'>
                {/* Left: Animated Score Ring */}
                <div className='flex flex-col items-center gap-3 flex-shrink-0'>
                  <div className='relative w-40 h-40'>
                    <svg className='w-40 h-40 -rotate-90' viewBox='0 0 120 120'>
                      <circle
                        cx='60'
                        cy='60'
                        r='54'
                        fill='none'
                        stroke='#e5e7eb'
                        className='dark:stroke-gray-700'
                        strokeWidth='8'
                      />
                      <circle
                        cx='60'
                        cy='60'
                        r='54'
                        fill='none'
                        stroke={isPassed ? '#22c55e' : '#ef4444'}
                        strokeWidth='8'
                        strokeLinecap='round'
                        strokeDasharray={`${2 * Math.PI * 54}`}
                        strokeDashoffset={`${2 * Math.PI * 54 * (1 - percentage / 100)}`}
                        style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
                      />
                    </svg>
                    <div className='absolute inset-0 flex flex-col items-center justify-center'>
                      <span className='text-3xl font-bold text-gray-900 dark:text-white'>
                        {percentage}%
                      </span>
                    </div>
                  </div>
                  {hasPassingScore && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold ${
                        isPassed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {isPassed ? '✓ ĐẠT' : '✗ CHƯA ĐẠT'}
                    </span>
                  )}
                </div>

                {/* Right: Info */}
                <div className='flex-1 text-center md:text-left'>
                  <h1 className='text-2xl font-bold text-gray-900 dark:text-white mb-1'>
                    {isPassed ? '🎉 Chúc mừng!' : '📚 Cố gắng lên!'}
                  </h1>
                  <p className='text-gray-600 dark:text-gray-300 mb-2'>
                    {examInfo.title || 'Kết quả bài thi'}
                  </p>
                  <p className='text-sm text-gray-400 mb-6'>
                    {completedAt.toLocaleString('vi-VN', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </p>
                  <div className='grid grid-cols-3 gap-3'>
                    <div className='rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 text-center'>
                      <div className='text-xl font-bold text-gray-900 dark:text-white'>
                        {formatScore(effectiveBackendGrade.score)}/
                        {formatScore(effectiveBackendGrade.maxScore)}
                      </div>
                      <div className='text-xs text-gray-500 mt-1'>Điểm số</div>
                    </div>
                    <div className='rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 text-center'>
                      <div className='text-xl font-bold text-gray-900 dark:text-white'>
                        {backendGrade.totalQuestions || ' - '}
                      </div>
                      <div className='text-xs text-gray-500 mt-1'>Tổng câu</div>
                    </div>
                    <div className='rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 text-center'>
                      <div className='text-xl font-bold text-gray-900 dark:text-white'>
                        {backendGrade.timeSpent
                          ? `${Math.floor(backendGrade.timeSpent / 60)}m`
                          : ' - '}
                      </div>
                      <div className='text-xs text-gray-500 mt-1'>Thời gian</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8'>
            {/* Correct Answers */}
            <Card className='hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-success/10 to-success/5 border-success/20'>
              <CardContent className='p-6'>
                <div className='flex items-start justify-between'>
                  <div>
                    <p className='text-sm text-muted-foreground mb-1'>Câu đúng</p>
                    <p className='text-3xl font-bold text-success'>{backendGrade.correctCount}</p>
                  </div>
                  <div className='w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center'>
                    <CheckCircle2 className='w-6 h-6 text-success' />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Incorrect Answers */}
            <Card className='hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-destructive/10 to-destructive/5 border-destructive/20'>
              <CardContent className='p-6'>
                <div className='flex items-start justify-between'>
                  <div>
                    <p className='text-sm text-muted-foreground mb-1'>Câu sai</p>
                    <p className='text-3xl font-bold text-destructive'>
                      {backendGrade.incorrectCount}
                    </p>
                  </div>
                  <div className='w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center'>
                    <XCircle className='w-6 h-6 text-destructive' />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Unanswered */}
            <Card className='hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-warning/10 to-warning/5 border-warning/20'>
              <CardContent className='p-6'>
                <div className='flex items-start justify-between'>
                  <div>
                    <p className='text-sm text-muted-foreground mb-1'>Bỏ qua</p>
                    <p className='text-3xl font-bold text-warning'>
                      {backendGrade.unansweredCount}
                    </p>
                  </div>
                  <div className='w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center'>
                    <FileText className='w-6 h-6 text-warning' />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Time Spent */}
            <Card className='hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-info/10 to-info/5 border-info/20'>
              <CardContent className='p-6'>
                <div className='flex items-start justify-between'>
                  <div>
                    <p className='text-sm text-muted-foreground mb-1'>Thời gian</p>
                    <p className='text-3xl font-bold text-info'>
                      {Math.floor(backendGrade.timeSpent / 60)}'
                    </p>
                  </div>
                  <div className='w-12 h-12 rounded-xl bg-info/10 flex items-center justify-center'>
                    <Clock className='w-6 h-6 text-info' />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Essay Questions Notification */}
          {hasEssayQuestions && answeredEssayQuestions > 0 && (
            <Card className='mb-8 border-purple-200 bg-gradient-to-br from-purple-50/50 via-purple-50/30 to-purple-50/50 dark:from-purple-900/20 dark:via-purple-900/10 dark:to-purple-900/20'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <div className='w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-xl flex items-center justify-center flex-shrink-0'>
                    <AlertCircle className='w-6 h-6 text-purple-600 dark:text-purple-400' />
                  </div>
                  <div className='flex-1'>
                    <h3 className='text-lg font-semibold text-purple-900 dark:text-purple-100 mb-2'>
                      Câu hỏi tự luận chưa được chấm điểm
                    </h3>
                    <p className='text-sm text-purple-700 dark:text-purple-300 mb-3 leading-relaxed'>
                      Bạn đã hoàn thành <strong>{answeredEssayQuestions} câu hỏi tự luận</strong>.
                      Những câu hỏi này sẽ được giáo viên chấm điểm thủ công và cập nhật kết quả
                      sau.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Detailed Information */}
          <Card className='mb-8 bg-surface-light dark:bg-surface-dark border border-gray-200 dark:border-gray-700 shadow-sm'>
            <CardHeader>
              <CardTitle className='flex items-center gap-2 text-gray-900 dark:text-white'>
                <TrendingUp className='w-5 h-5 text-primary' />
                Thông tin chi tiết
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              {/* Student Info */}
              <div className='flex items-center gap-4 p-4 rounded-lg bg-muted/50'>
                <div className='w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center'>
                  <User className='w-6 h-6 text-primary' />
                </div>
                <div>
                  <p className='text-sm text-muted-foreground'>Học sinh</p>
                  <p className='font-semibold text-foreground'>{userName}</p>
                </div>
              </div>

              {/* Exam Info */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='flex items-center gap-3'>
                  <Calendar className='w-5 h-5 text-primary' />
                  <div>
                    <p className='text-sm text-muted-foreground'>Ngày thi</p>
                    <p className='font-medium text-foreground'>
                      {completedAt.toLocaleDateString('vi-VN')} lúc{' '}
                      {completedAt.toLocaleTimeString('vi-VN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>

                <div className='flex items-center gap-3'>
                  <FileText className='w-5 h-5 text-primary' />
                  <div>
                    <p className='text-sm text-muted-foreground'>Tổng số câu</p>
                    <p className='font-medium text-foreground'>
                      {backendGrade.answeredQuestions}/{backendGrade.totalQuestions} câu đã trả lời
                    </p>
                  </div>
                </div>

                <div className='flex items-center gap-3'>
                  <Clock className='w-5 h-5 text-primary' />
                  <div>
                    <p className='text-sm text-muted-foreground'>Thời gian làm bài</p>
                    <p className='font-medium text-foreground'>
                      {Math.floor(backendGrade.timeSpent / 60)} phút {backendGrade.timeSpent % 60}{' '}
                      giây
                    </p>
                  </div>
                </div>

                <div className='flex items-center gap-3'>
                  <Award className='w-5 h-5 text-primary' />
                  <div>
                    <p className='text-sm text-muted-foreground'>Điểm đạt được</p>
                    <p className='font-medium text-foreground'>
                      {formatScore(effectiveBackendGrade.score)}/
                      {formatScore(effectiveBackendGrade.maxScore)} điểm
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Question Details Section - Only show if allowed */}
          {questions && questions.length > 0 && canViewDetailedResults && (
            <Card className='mb-8 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10 dark:from-primary/10 dark:via-accent/10 dark:to-primary/20'>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <FileText className='w-5 h-5 text-primary' />
                  Chi tiết câu hỏi và đáp án
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                {questions.map((question: any, index: number) => {
                  const studentAnswer = resultsData.answers?.[question.id];
                  const questionGrade = backendGrade.questionGrades?.find(
                    (g: any) => g.questionId === question.id
                  );
                  const isCorrect = questionGrade?.isCorrect ?? false;
                  const pointsAwarded = questionGrade?.pointsAwarded ?? 0;
                  const maxPoints = questionGrade?.maxPoints ?? question.points ?? 0;

                  // Use questionDetails from backend if available (for free exams)
                  const questionDetails = questionGrade?.questionDetails;

                  // Use questionDetails data if available, otherwise fallback to question object
                  const displayQuestion = questionDetails || question;

                  return (
                    <Card
                      key={question.id || index}
                      className={`border-2 ${
                        isCorrect
                          ? 'border-success/30 bg-success/5'
                          : 'border-destructive/30 bg-destructive/5'
                      }`}
                    >
                      <CardContent className='p-6'>
                        <div className='flex items-start gap-4 mb-4'>
                          <div
                            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                              isCorrect
                                ? 'bg-success/20 text-success'
                                : 'bg-destructive/20 text-destructive'
                            }`}
                          >
                            {isCorrect ? (
                              <CheckCircle2 className='w-6 h-6' />
                            ) : (
                              <XCircle className='w-6 h-6' />
                            )}
                          </div>
                          <div className='flex-1'>
                            <div className='flex items-center gap-3 mb-3'>
                              <span className='text-lg font-bold text-foreground'>
                                Câu {index + 1}
                              </span>
                              <Badge
                                className={
                                  isCorrect
                                    ? 'bg-success text-success-foreground'
                                    : 'bg-destructive text-destructive-foreground'
                                }
                              >
                                {isCorrect ? 'Đúng' : 'Sai'} ({formatScore(pointsAwarded)}/
                                {formatScore(maxPoints)} điểm)
                              </Badge>
                            </div>
                            <div className='text-sm text-foreground mb-4'>
                              <HtmlContent
                                content={
                                  displayQuestion.stem ||
                                  displayQuestion.context ||
                                  question.stem ||
                                  question.context ||
                                  ''
                                }
                              />
                            </div>
                          </div>
                        </div>

                        {/* Display question options and answers based on question type */}
                        {(question.type === 'mc' || question.type === 'mcq') &&
                        displayQuestion.options ? (
                          <div className='space-y-4'>
                            <p className='text-sm font-semibold text-foreground mb-2'>
                              Các lựa chọn:
                            </p>
                            {(() => {
                              // Find correct option letter before mapping
                              const correctOptionIndex = displayQuestion.options?.findIndex(
                                (opt: any) => opt.isCorrect
                              );
                              const correctOptionLetter =
                                correctOptionIndex !== undefined && correctOptionIndex >= 0
                                  ? String.fromCharCode(65 + correctOptionIndex)
                                  : null;

                              return (
                                <>
                                  {displayQuestion.options.map((option: any, optIndex: number) => {
                                    const optionId = option.id;
                                    const optionContent = option.content;
                                    const isCorrectOption = option.isCorrect || false;

                                    // Check if this is student's answer
                                    const studentValue = studentAnswer?.value;
                                    const isStudentAnswer =
                                      studentValue === optionId ||
                                      studentValue === optIndex ||
                                      (typeof studentValue === 'string' &&
                                        studentValue === String(optIndex)) ||
                                      (typeof studentValue === 'number' &&
                                        studentValue === optIndex);

                                    return (
                                      <div
                                        key={optIndex}
                                        className={`p-3 rounded-lg border-2 ${
                                          isCorrectOption
                                            ? 'bg-success/10 border-success/50 text-success-foreground'
                                            : isStudentAnswer && !isCorrectOption
                                              ? 'bg-destructive/10 border-destructive/50 text-destructive-foreground'
                                              : 'bg-muted/50 border-border'
                                        }`}
                                      >
                                        <div className='flex items-start gap-2'>
                                          <span className='font-semibold flex-shrink-0'>
                                            {String.fromCharCode(65 + optIndex)}.
                                          </span>
                                          <div className='flex-1 min-w-0'>
                                            <HtmlContent content={optionContent || ''} />
                                          </div>
                                          <div className='flex-shrink-0 flex flex-col gap-1'>
                                            {isCorrectOption && (
                                              <Badge className='bg-success text-success-foreground text-xs whitespace-nowrap'>
                                                {isStudentAnswer
                                                  ? '✓ Bạn chọn (đúng)'
                                                  : '✓ Đáp án đúng'}
                                              </Badge>
                                            )}
                                            {isStudentAnswer && !isCorrectOption && (
                                              <Badge className='bg-destructive text-destructive-foreground text-xs whitespace-nowrap'>
                                                ✗ Bạn chọn
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* Always show correct answer at the end */}
                                  {correctOptionLetter && (
                                    <div className='mt-4 p-3 rounded-lg bg-info/10 border border-info/30'>
                                      <p className='text-sm text-info font-semibold'>
                                        💡 Đáp án đúng là:{' '}
                                        <span className='text-primary font-bold'>
                                          {correctOptionLetter}
                                        </span>
                                      </p>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        ) : (question.type === 'trueFalse' || question.type === 'tf_matrix') &&
                          displayQuestion.trueFalseItems ? (
                          <div className='space-y-3'>
                            <p className='text-sm font-semibold text-foreground mb-2'>
                              Đáp án của bạn và đáp án đúng:
                            </p>
                            {displayQuestion.trueFalseItems.map((item: any, itemIndex: number) => {
                              const studentItemAnswer =
                                studentAnswer?.value?.[item.id] ??
                                studentAnswer?.value?.[String(itemIndex)] ??
                                null;
                              const isItemCorrect = studentItemAnswer === item.correct;

                              return (
                                <div
                                  key={item.id || itemIndex}
                                  className={`p-3 rounded-lg border-2 ${
                                    isItemCorrect
                                      ? 'bg-success/10 border-success/30'
                                      : 'bg-destructive/10 border-destructive/30'
                                  }`}
                                >
                                  <div className='mb-2'>
                                    <HtmlContent content={item.statement || ''} />
                                  </div>
                                  <div className='flex items-center gap-4 text-sm'>
                                    <div>
                                      <span className='text-muted-foreground'>Bạn chọn: </span>
                                      <span
                                        className={`font-semibold ${
                                          isItemCorrect ? 'text-success' : 'text-destructive'
                                        }`}
                                      >
                                        {studentItemAnswer === null ||
                                        studentItemAnswer === undefined
                                          ? 'Chưa trả lời'
                                          : studentItemAnswer
                                            ? 'Đúng'
                                            : 'Sai'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className='text-muted-foreground'>Đáp án đúng: </span>
                                      <span className='font-semibold text-success'>
                                        {item.correct ? 'Đúng' : 'Sai'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : question.type === 'matching' ? (
                          <div className='space-y-4'>
                            <p className='text-sm font-semibold text-foreground mb-3'>
                              Kết quả ghép nối:
                            </p>

                            {/* Get matching data - prefer questionDetails from backend */}
                            {(() => {
                              // Use questionDetails from backend if available (preferred)
                              let prompts: any[] = [];
                              let options: any[] = [];
                              let correctMapping: Record<string, string> = {};

                              if (
                                questionDetails?.matchingPrompts &&
                                questionDetails?.matchingOptions
                              ) {
                                prompts = questionDetails.matchingPrompts;
                                options = questionDetails.matchingOptions;
                                correctMapping = questionDetails.matchingCorrect || {};
                              } else {
                                // Fallback to question object
                                prompts =
                                  question.matchingPrompts ||
                                  question.content?.matchingPrompts ||
                                  question.leftItems ||
                                  (question.content?.leftItems || []).map(
                                    (item: any, idx: number) => ({
                                      id: item.key || item.id || `left-${idx}`,
                                      label: item.text || item.label || item.content || '',
                                    })
                                  ) ||
                                  [];

                                options =
                                  question.matchingOptions ||
                                  question.content?.matchingOptions ||
                                  question.rightItems ||
                                  question.right_pool ||
                                  (question.content?.rightItems || []).map(
                                    (item: any, idx: number) => ({
                                      id: item.key || item.id || `right-${idx}`,
                                      label: item.text || item.label || item.content || '',
                                    })
                                  ) ||
                                  [];

                                correctMapping =
                                  question.matchingCorrect ||
                                  question.content?.matchingCorrect ||
                                  question.answer_map ||
                                  {};
                              }

                              // Student answer might be directly in value or nested
                              const studentMapping =
                                studentAnswer?.value || studentAnswer?.matchingAnswers || {};

                              if (!prompts.length || !options.length) {
                                return (
                                  <div className='p-4 bg-yellow-50 border border-yellow-200 rounded-lg'>
                                    <p className='text-sm text-yellow-700'>
                                      ⚠️ Không có dữ liệu ghép nối. Debug: prompts={prompts.length},
                                      options={options.length}
                                    </p>
                                    <pre className='text-xs mt-2 overflow-auto'>
                                      {JSON.stringify(
                                        {
                                          prompts,
                                          options,
                                          correctMapping,
                                          studentMapping,
                                          question,
                                        },
                                        null,
                                        2
                                      )}
                                    </pre>
                                  </div>
                                );
                              }

                              // Create option lookup with multiple key formats
                              const optionMap = new Map<string, string>();
                              options.forEach((opt: any) => {
                                const optId =
                                  typeof opt === 'object' ? opt.id || opt.key : String(opt);
                                const optLabel =
                                  typeof opt === 'object'
                                    ? opt.label || opt.text || opt.content
                                    : String(opt || '');
                                if (optId) {
                                  optionMap.set(String(optId), String(optLabel || ''));
                                  // Also add with different key formats for lookup
                                  if (typeof opt === 'object' && opt.key) {
                                    optionMap.set(String(opt.key), String(optLabel || ''));
                                  }
                                }
                              });

                              return (
                                <div className='space-y-3'>
                                  {prompts.map((prompt: any, promptIndex: number) => {
                                    const promptId = String(
                                      typeof prompt === 'object'
                                        ? prompt.id || prompt.key
                                        : prompt || ''
                                    );
                                    const promptLabel = String(
                                      typeof prompt === 'object'
                                        ? prompt.label || prompt.text || prompt.content
                                        : prompt || ''
                                    );

                                    // Try multiple key formats for correct mapping
                                    const correctOptionId =
                                      correctMapping[promptId] ||
                                      correctMapping[prompt.key] ||
                                      null;

                                    // Try multiple key formats for student mapping
                                    const studentOptionId =
                                      studentMapping && typeof studentMapping === 'object'
                                        ? studentMapping[promptId] ||
                                          studentMapping[prompt.key] ||
                                          null
                                        : null;

                                    const isPairCorrect =
                                      correctOptionId &&
                                      studentOptionId &&
                                      String(correctOptionId) === String(studentOptionId);

                                    const correctOptionLabel = correctOptionId
                                      ? optionMap.get(String(correctOptionId)) || 'N/A'
                                      : null;
                                    const studentOptionLabel = studentOptionId
                                      ? optionMap.get(String(studentOptionId)) || 'N/A'
                                      : null;

                                    return (
                                      <div
                                        key={promptId || promptIndex}
                                        className={`p-3 rounded-lg border-2 ${
                                          isPairCorrect
                                            ? 'bg-success/10 border-success/30'
                                            : 'bg-destructive/10 border-destructive/30'
                                        }`}
                                      >
                                        <div className='flex items-start gap-3'>
                                          <span className='font-semibold text-foreground flex-shrink-0'>
                                            {promptIndex + 1}.
                                          </span>
                                          <div className='flex-1 min-w-0'>
                                            <div className='mb-2'>
                                              <p className='text-sm font-medium text-foreground'>
                                                <HtmlContent content={promptLabel || ''} />
                                              </p>
                                            </div>

                                            {/* Student's answer */}
                                            <div className='mb-2'>
                                              <p className='text-xs text-muted-foreground mb-1'>
                                                Bạn ghép với:
                                              </p>
                                              <div
                                                className={`p-2 rounded ${
                                                  isPairCorrect
                                                    ? 'bg-success/20 border border-success/40'
                                                    : 'bg-destructive/20 border border-destructive/40'
                                                }`}
                                              >
                                                <span
                                                  className={`text-sm font-medium ${
                                                    isPairCorrect
                                                      ? 'text-success'
                                                      : 'text-destructive'
                                                  }`}
                                                >
                                                  {studentOptionLabel ? (
                                                    <HtmlContent content={studentOptionLabel} />
                                                  ) : (
                                                    <span className='italic'>Chưa ghép</span>
                                                  )}
                                                </span>
                                                {!isPairCorrect && (
                                                  <Badge className='ml-2 bg-destructive text-destructive-foreground text-xs'>
                                                    ✗ Sai
                                                  </Badge>
                                                )}
                                                {isPairCorrect && (
                                                  <Badge className='ml-2 bg-success text-success-foreground text-xs'>
                                                    ✓ Đúng
                                                  </Badge>
                                                )}
                                              </div>
                                            </div>

                                            {/* Correct answer - always show if there's a correct mapping */}
                                            {correctOptionLabel && (
                                              <div>
                                                <p className='text-xs text-muted-foreground mb-1'>
                                                  Đáp án đúng:
                                                </p>
                                                <div className='p-2 rounded bg-success/20 border border-success/40'>
                                                  <span className='text-sm font-medium text-success'>
                                                    <HtmlContent content={correctOptionLabel} />
                                                  </span>
                                                  <Badge className='ml-2 bg-success text-success-foreground text-xs'>
                                                    ✓ Đúng
                                                  </Badge>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                        ) : question.type === 'short_text' ||
                          question.type === 'short_numeric' ||
                          question.type === 'short' ||
                          question.type === 'fillBlank' ? (
                          <div className='space-y-3'>
                            {/* Student's answer - always show */}
                            <div>
                              <p className='text-sm font-semibold text-foreground mb-2'>
                                Đáp án của bạn:
                              </p>
                              <div
                                className={`p-3 rounded-lg border-2 ${
                                  isCorrect
                                    ? 'bg-success/10 border-success/50'
                                    : 'bg-destructive/10 border-destructive/50'
                                }`}
                              >
                                {(() => {
                                  // Extract student answer - handle multiple formats
                                  const studentValue = studentAnswer?.value;
                                  if (!studentValue) {
                                    return (
                                      <span className='text-muted-foreground italic'>
                                        Chưa trả lời
                                      </span>
                                    );
                                  }

                                  // For cloze/fillBlank questions in clusters, extract the specific blank answer
                                  // Use blankId from questionDetails.fillBlankAnswers to get the correct answer for this question
                                  if (
                                    (question.type === 'cloze' || question.type === 'fillBlank') &&
                                    questionDetails?.fillBlankAnswers &&
                                    Array.isArray(questionDetails.fillBlankAnswers) &&
                                    questionDetails.fillBlankAnswers.length > 0
                                  ) {
                                    // Get the blankId for this specific question
                                    const blankId = questionDetails.fillBlankAnswers[0]?.blankId;

                                    if (
                                      blankId &&
                                      typeof studentValue === 'object' &&
                                      studentValue !== null
                                    ) {
                                      // Extract blank number from blankId (e.g., "blank0" -> 0, "blank1" -> 1)
                                      const blankIdMatch = blankId.match(/blank(\d+)/i);
                                      if (blankIdMatch) {
                                        const blankNumber = parseInt(blankIdMatch[1], 10);
                                        let answerByBlankId: any = undefined;

                                        // For cluster questions, each question's value object contains ALL cluster answers
                                        // The blankId from questionDetails is 0-based (blank0, blank1, blank2, ...)
                                        // But the answer for THIS question is stored at blankId + 1 (blank1, blank2, blank3, ...)
                                        // Example: Question 1 has blankId="blank0", but answer is at "blank1"
                                        // Example: Question 2 has blankId="blank1", but answer is at "blank2"

                                        // Try with +1 offset first (most common case: DB uses 0-based, user answers use 1-based)
                                        const answerBlankId = `blank${blankNumber + 1}`;
                                        answerByBlankId = studentValue[answerBlankId];

                                        // Fallback: Try exact blankId match (if no offset)
                                        if (
                                          answerByBlankId === undefined ||
                                          answerByBlankId === null ||
                                          answerByBlankId === ''
                                        ) {
                                          answerByBlankId = studentValue[blankId];
                                        }

                                        // Fallback: Try with -1 offset (if reverse offset case)
                                        if (
                                          answerByBlankId === undefined ||
                                          answerByBlankId === null ||
                                          answerByBlankId === ''
                                        ) {
                                          if (blankNumber > 0) {
                                            const offsetMinus1 = `blank${blankNumber - 1}`;
                                            if (studentValue[offsetMinus1] !== undefined) {
                                              answerByBlankId = studentValue[offsetMinus1];
                                            }
                                          }
                                        }

                                        if (
                                          answerByBlankId !== undefined &&
                                          answerByBlankId !== null &&
                                          answerByBlankId !== ''
                                        ) {
                                          return (
                                            <span
                                              className={`font-semibold ${
                                                isCorrect ? 'text-success' : 'text-destructive'
                                              }`}
                                            >
                                              {String(answerByBlankId)}
                                            </span>
                                          );
                                        }
                                      }
                                    }
                                  }

                                  // If it's an object (fillAnswers format), extract values
                                  if (typeof studentValue === 'object' && studentValue !== null) {
                                    const entries = Object.entries(studentValue);
                                    // Filter out blankId strings to avoid showing them as answers
                                    const validEntries = entries.filter(
                                      ([key]) => !key.match(/^blank\d+$/i)
                                    );
                                    if (validEntries.length > 0) {
                                      return (
                                        <span
                                          className={`font-semibold ${
                                            isCorrect ? 'text-success' : 'text-destructive'
                                          }`}
                                        >
                                          {validEntries.map(([key, val]) => String(val)).join(', ')}
                                        </span>
                                      );
                                    }
                                  }

                                  // Otherwise it's a string or number
                                  return (
                                    <span
                                      className={`font-semibold ${
                                        isCorrect ? 'text-success' : 'text-destructive'
                                      }`}
                                    >
                                      {String(studentValue)}
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>

                            {/* Correct answer - always show */}
                            <div>
                              <p className='text-sm font-semibold text-foreground mb-2'>
                                Đáp án đúng:
                              </p>
                              <div className='p-3 rounded-lg bg-success/10 border-2 border-success/50'>
                                <div className='space-y-1'>
                                  {(() => {
                                    // Use questionDetails from backend if available (preferred)
                                    if (questionDetails) {
                                      // For short_numeric: use numericAnswers from questionDetails
                                      if (
                                        question.type === 'short_numeric' ||
                                        question.type === 'short'
                                      ) {
                                        if (
                                          questionDetails.numericAnswers &&
                                          Array.isArray(questionDetails.numericAnswers) &&
                                          questionDetails.numericAnswers.length > 0
                                        ) {
                                          return questionDetails.numericAnswers.map(
                                            (a: any, idx: number) => {
                                              const value =
                                                typeof a === 'object' ? (a.value ?? a) : a;
                                              const tolerance =
                                                typeof a === 'object' ? (a.tolerance ?? 0) : 0;
                                              if (tolerance > 0) {
                                                return `${value} (±${tolerance})`;
                                              }
                                              return String(value);
                                            }
                                          );
                                        }
                                      }

                                      // For short_text: use textAnswers from questionDetails
                                      if (question.type === 'short_text') {
                                        if (
                                          questionDetails.textAnswers &&
                                          Array.isArray(questionDetails.textAnswers) &&
                                          questionDetails.textAnswers.length > 0
                                        ) {
                                          return questionDetails.textAnswers.map((a: any) => {
                                            if (typeof a === 'object') {
                                              return String(a.text || a.value || a);
                                            }
                                            return String(a);
                                          });
                                        }
                                      }

                                      // For fillBlank: use fillBlankAnswers from questionDetails (only for actual fillBlank questions)
                                      if (
                                        question.type === 'fillBlank' ||
                                        question.type === 'cloze'
                                      ) {
                                        if (
                                          questionDetails.fillBlankAnswers &&
                                          Array.isArray(questionDetails.fillBlankAnswers) &&
                                          questionDetails.fillBlankAnswers.length > 0
                                        ) {
                                          return questionDetails.fillBlankAnswers.map((a: any) => {
                                            if (typeof a === 'object') {
                                              return String(a.answer || a.correctAnswer || a.value);
                                            }
                                            return String(a);
                                          });
                                        }
                                      }

                                      // If questionDetails exists but no answers found, log for debugging
                                      console.warn(
                                        `[Question ${index + 1}] questionDetails exists but no answers found:`,
                                        {
                                          type: question.type,
                                          questionDetails,
                                        }
                                      );
                                    }

                                    // Fallback to question object (only if questionDetails not available or empty)
                                    const correctAnswers: string[] = [];

                                    // For short_numeric: NEVER use fillBlankAnswers (those are blankIds, not answers)
                                    if (
                                      question.type === 'short_numeric' ||
                                      question.type === 'short'
                                    ) {
                                      // Try to parse content if it's a string
                                      let parsedContent: any = {};
                                      if (question.content) {
                                        if (typeof question.content === 'string') {
                                          try {
                                            parsedContent = JSON.parse(question.content);
                                          } catch {
                                            parsedContent = {};
                                          }
                                        } else {
                                          parsedContent = question.content;
                                        }
                                      }

                                      const numericAnswers =
                                        question.numericAnswers ||
                                        parsedContent?.numericAnswers ||
                                        [];
                                      if (
                                        Array.isArray(numericAnswers) &&
                                        numericAnswers.length > 0
                                      ) {
                                        correctAnswers.push(
                                          ...numericAnswers.map((a: any) => {
                                            const value =
                                              typeof a === 'object' ? (a.value ?? a) : a;
                                            const tolerance =
                                              typeof a === 'object' ? (a.tolerance ?? 0) : 0;
                                            if (tolerance > 0) {
                                              return `${value} (±${tolerance})`;
                                            }
                                            return String(value);
                                          })
                                        );
                                      }
                                    }

                                    // For short_text
                                    if (question.type === 'short_text') {
                                      let parsedContent: any = {};
                                      if (question.content) {
                                        if (typeof question.content === 'string') {
                                          try {
                                            parsedContent = JSON.parse(question.content);
                                          } catch {
                                            parsedContent = {};
                                          }
                                        } else {
                                          parsedContent = question.content;
                                        }
                                      }

                                      const textAnswers =
                                        question.textAnswers || parsedContent?.textAnswers || [];
                                      if (Array.isArray(textAnswers) && textAnswers.length > 0) {
                                        correctAnswers.push(
                                          ...textAnswers.map((a: any) => {
                                            if (typeof a === 'object') {
                                              return String(a.text || a.value || a);
                                            }
                                            return String(a);
                                          })
                                        );
                                      }
                                    }

                                    // For fillBlank/cloze: can use fillBlankAnswers
                                    if (
                                      question.type === 'fillBlank' ||
                                      question.type === 'cloze'
                                    ) {
                                      if (
                                        question.fillBlankAnswers &&
                                        Array.isArray(question.fillBlankAnswers)
                                      ) {
                                        correctAnswers.push(
                                          ...question.fillBlankAnswers.map((a: any) => {
                                            if (typeof a === 'object') {
                                              return String(a.correctAnswer || a.answer || a.value);
                                            }
                                            return String(a);
                                          })
                                        );
                                      }
                                    }

                                    if (correctAnswers.length === 0) {
                                      console.warn(
                                        `[Question ${index + 1}] No correct answer found:`,
                                        {
                                          type: question.type,
                                          questionId: question.id,
                                          questionDetails,
                                          question: {
                                            numericAnswers: question.numericAnswers,
                                            content: question.content,
                                          },
                                        }
                                      );
                                      return ['Không tìm thấy đáp án đúng'];
                                    }

                                    return [
                                      ...new Set(
                                        correctAnswers.filter(
                                          a => a && String(a).trim() && !a.includes('-short')
                                        )
                                      ),
                                    ]; // Filter out blankIds
                                  })().map((answer: string, idx: number) => (
                                    <div key={idx} className='text-success font-semibold'>
                                      • {String(answer)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}

                        {/* Explanation if available */}
                        {question.explanation && (
                          <div className='mt-4 p-3 rounded-lg bg-info/10 border border-info/30'>
                            <p className='text-sm font-semibold text-info mb-1'>Giải thích:</p>
                            <p className='text-sm text-foreground'>
                              <HtmlContent content={question.explanation} />
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className='flex flex-wrap gap-4 justify-center'>
            <Button onClick={handleTryAgain} size='lg' variant='outline' className='min-w-[200px]'>
              <RotateCcw className='w-5 h-5 mr-2' />
              Làm lại bài thi
            </Button>

            <Button
              onClick={handleSendEmail}
              disabled={isSendingEmail}
              size='lg'
              variant='outline'
              className='min-w-[200px] border-primary/20 text-primary hover:bg-primary/5 hover:text-primary'
            >
              <Mail className='w-5 h-5 mr-2' />
              {isSendingEmail ? 'Đang gửi...' : 'Gửi kết quả qua Email'}
            </Button>

            <Button
              onClick={handleBackHome}
              size='lg'
              className='min-w-[200px] bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm'
            >
              <Home className='w-5 h-5 mr-2' />
              Về trang chủ
            </Button>
          </div>

          {/* Footer Note */}
          <div className='mt-8 text-center'>
            <p className='text-sm text-muted-foreground'>
              Kết quả đã được lưu vào hệ thống. Bạn có thể xem lại trong mục "Lịch sử thi".
            </p>
          </div>
        </div>
      </main>
      <UnifiedFooter />
    </div>
  );
}
